import { useCallback, useEffect } from "react";
import { spaceActions } from "redux/Ldb/actions/spaceActions";
import { useAppDispatch, useAppSelector } from "../storeHooks";
import { SpaceGuestInvitePreviewData } from "redux/Ldb/types/spaceTypes";

const useSpaceGuestInvitePreview = ({
    path: { token },
    options: { skip, unwrap = false },
}: UseSpaceGuestInvitePreviewParams): UseSpaceGuestInvitePreviewReturn => {
    const dispatch = useAppDispatch();

    const {
        gettingSpaceGuestInvitePreview,
        gotSpaceGuestInvitePreviewSuccess,
        gotSpaceGuestInvitePreviewFailure,
    } = useAppSelector((state) => state.space);

    const getSpaceGuestInvitePreview = useCallback((): Promise<SpaceGuestInvitePreviewData> => {
        return new Promise((resolve, reject) => {
            // Reset any previous state
            dispatch(spaceActions.resetGetSpaceGuestInvitePreview());

            // Start the Redux action
            dispatch(spaceActions.getSpaceGuestInvitePreview(token));

            // Poll for state changes
            const checkState = () => {
                const currentState = dispatch((dispatch, getState) => {
                    const state = getState();
                    return {
                        isLoading: state.space.gettingSpaceGuestInvitePreview,
                        success: state.space.gotSpaceGuestInvitePreviewSuccess,
                        error: state.space.gotSpaceGuestInvitePreviewFailure,
                    };
                });

                if (currentState.error) {
                    reject(currentState.error);
                } else if (currentState.success && !currentState.isLoading) {
                    resolve(currentState.success);
                } else if (
                    !currentState.isLoading &&
                    !currentState.success &&
                    !currentState.error
                ) {
                    reject("Request failed without error or success response");
                } else {
                    // Still loading, check again
                    setTimeout(checkState, 100);
                }
            };

            checkState();
        });
    }, [dispatch, token]);

    const resetGetSpaceGuestInvitePreview = useCallback(() => {
        dispatch(spaceActions.resetGetSpaceGuestInvitePreview());
    }, [dispatch]);

    const refetchGetSpaceGuestInvitePreview = useCallback(() => {
        getSpaceGuestInvitePreview();
    }, [getSpaceGuestInvitePreview]);

    async function getSpaceGuestInvitePreviewWrapper() {
        await getSpaceGuestInvitePreview().catch((error) => {
            console.error(error);
        });
    }

    useEffect(() => {
        if (skip) return;
        if (unwrap) {
            getSpaceGuestInvitePreview();
        } else {
            getSpaceGuestInvitePreviewWrapper();
        }
    }, []);

    return {
        isSuccess: Boolean(gotSpaceGuestInvitePreviewSuccess),
        isError: Boolean(gotSpaceGuestInvitePreviewFailure),
        isLoading: gettingSpaceGuestInvitePreview,
        data:
            (gotSpaceGuestInvitePreviewSuccess?.data?.invite as SpaceGuestInvitePreviewData) ||
            null,
        error: gotSpaceGuestInvitePreviewFailure,
        resetGetSpaceGuestInvitePreview,
        refetch: refetchGetSpaceGuestInvitePreview,
    };
};

export default useSpaceGuestInvitePreview;

export interface UseSpaceGuestInvitePreviewReturn {
    isSuccess: boolean;
    isError: boolean;
    isLoading: boolean;
    data: SpaceGuestInvitePreviewData | null;
    error: any;
    resetGetSpaceGuestInvitePreview: () => void;
    refetch: () => void;
}

export interface UseSpaceGuestInvitePreviewParams {
    path: {
        token: string;
    };
    options: {
        skip?: boolean;
        unwrap?: boolean;
    };
}
